import { useId } from "react";
import type { ReactElement } from "react";
import type { LayerVisibility } from "../../state/layerState";
import type { UtilityInference } from "@shared/api";
import type { InspectorPresentation } from "./BaseLayersCard";
import { InspectorRow } from "./InspectorRow";
import { SubsectionHeader } from "./SubsectionHeader";
import { UTILITY_SERVICES_ORDER } from "./utilityInferenceLabels";
import { UtilityInferenceRow } from "./UtilityInferenceRow";
import styles from "./UtilitiesLayersCard.module.css";

function orderedInferences(rows: UtilityInference[] | null): UtilityInference[] {
  if (!rows || rows.length === 0) return [];
  const bySvc = new Map(rows.map((r) => [r.service, r] as const));
  return UTILITY_SERVICES_ORDER.flatMap((svc) =>
    bySvc.has(svc) ? [bySvc.get(svc)!] : []
  );
}

export interface UtilitiesLayersCardProps {
  visibility: LayerVisibility;
  onUtilitiesOverlayToggle: (value: boolean) => void;
  utilityInferences: UtilityInference[] | null;
  presentation?: InspectorPresentation;
  /** Visibility keys whose value differs from the viewer default (accent dot). */
  modifiedKeys?: ReadonlySet<string>;
}

export function UtilitiesLayersCard({
  visibility,
  onUtilitiesOverlayToggle,
  utilityInferences,
  modifiedKeys
}: UtilitiesLayersCardProps): ReactElement {
  const reactId = useId();
  const normalized = orderedInferences(utilityInferences);

  return (
    <div>
      <InspectorRow
        label="Infrastructure on map"
        labelWeight="normal"
        checkboxId={`${reactId}-util-overlay`}
        checked={visibility.utilitiesOverlay}
        modified={modifiedKeys?.has("utilitiesOverlay") === true}
        onCheckedChange={(v) => {
          onUtilitiesOverlayToggle(v);
        }}
      />
      <SubsectionHeader label="Likely service" />
      {normalized.length > 0 ? (
        <>
          <div className={styles.inferenceList}>
            {normalized.map((inf) => (
              <UtilityInferenceRow key={inf.service} inference={inf} />
            ))}
          </div>
          <hr className={styles.footerRule} />
          <p className={styles.disclaimer}>
            Inferred from public data — confirm with local utilities before
            purchase.
          </p>
        </>
      ) : (
        <p className={styles.emptyState}>No data</p>
      )}
    </div>
  );
}
